import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = note => note.internalid;

const clientNotesSlice = createSlice({
    name: 'clientNotes',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            const byClient = groupBy(ensureArray(action.payload.data), 'clientid');
            action.payload.ids.forEach(id => {
                if (byClient[id]) {
                    state.data[id] = byClient[id];
                } else {
                    delete state.data[id];
                }
            })
        },
        addOrUpdate: (state, action) => {
            const notes = ensureArray(action.payload);
            const byClient = groupBy(notes, 'clientid');
            Object.entries(byClient).forEach(([ clientid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[clientid] = (state.data[clientid]?.filter(n => !ids.has(key(n))) ?? []).concat(notes);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byClient = groupBy(toRemove, 'clientid');
            Object.entries(byClient).forEach(([clientid, notes]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[clientid] = state.data[clientid].filter(n => !ids.has(key(n)));
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => clientNotesSlice.getInitialState());
    }
});


// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'clientNotes', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'clientNotes');

const fetchClientNotes = clientid => network => network.get('/api/v1/clientnotes/' + encodeURIComponent(clientid), data => clientNotesSlice.actions.set({ data, ids: [clientid] }), visualizeFailure('get'));
export const addClientNote = (network, data) => network.post('/api/v1/clientnote', data, clientNotesSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectAllClientNoteData = state => state.clientNotes.data;
const selectClientNotes = clientid => createSelector([selectAllClientNoteData], notes => notes[clientid] ?? []);
// #endregion

const listen = {
    clientNoteCreated: (note, { dispatch, websocket }) => {
        markFetched(websocket, 'client_note', key(note))
        dispatch(clientNotesSlice.actions.addOrUpdate(note))
    }
}

const clientInitNotesByID = initMapFactory(fetchClientNotes, listen);
registerEntityBasedFetch('client', clientInitNotesByID);

const cleanup = { root: clientNotesSlice.name, all: state => Object.values(selectAllClientNoteData(state)).flat(), removeAll: clientNotesSlice.actions.remove, key };
export const [useClientNotes] = dataHookFactory(selectClientNotes, clientInitNotesByID, { cleanup });

const reducer = { name: clientNotesSlice.name, reducer: clientNotesSlice.reducer }
export default reducer;